import FolderOutlined from "@mui/icons-material/FolderOutlined";
import PermMediaOutlined from "@mui/icons-material/PermMediaOutlined";
import { Box, TextField, Tooltip, Typography } from "@mui/material";
import { type TreeItemProps, TreeItem } from "@mui/x-tree-view";
import React, { useCallback, useEffect, useRef, useState, type DragEvent } from "react";
import toast from "react-hot-toast";
import { useSupabaseCallback } from "../../../server/supabase/hooks";
import type { UUID } from "../../../utils/uuid";
import { ConditionalWrapper } from "../../generic/components/conditional-wrapper";
import { useDragState } from "../hooks/use-drag-state";
import { SupabaseFolderService } from "../server/folder-service";
import { ROOT_ID } from "../utils";
import { CustomTreeItemMenu } from "./custom-tree-item-menu";

type CustomTreeItemProps = Omit<TreeItemProps, "onDragOver" | "onDragStart" | "onDragEnd"> & {
  reloadFolders?: () => void;
  onCreateSubFolder?: (parentFolderId: UUID | null) => void;
  onArchive?: (folderId: UUID | null) => void;
  onMove?: (folderId: UUID) => void;
  onItemDragStart?: (folderId: UUID, event: DragEvent) => void;
  onItemDragOver?: (event: DragEvent) => void;
  onItemDrop?: (folderId: UUID | null, event: DragEvent) => void;
};

export const CustomTreeItem = ({
  itemId,
  label,
  slotProps,
  reloadFolders,
  onCreateSubFolder,
  onArchive,
  onMove,
  onItemDragStart,
  onItemDragOver,
  onItemDrop,
  ...rest
}: CustomTreeItemProps): React.ReactElement => {
  const [local, setLocal] = useState<string>(label as string);
  const lastCommittedNameRef = useRef<string>(label as string);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const shouldTruncate = local.length > 16;
  const isRoot = itemId === ROOT_ID;
  const menuOpen = Boolean(menuAnchor);

  const handleItemDragStart = useCallback(
    (event: DragEvent): void => {
      if (isRoot) {
        event.preventDefault();
        return;
      }
      onItemDragStart?.(itemId as UUID, event);
    },
    [isRoot, itemId, onItemDragStart],
  );

  const handleItemDrop = useCallback(
    (event: DragEvent): void => {
      const targetFolderId = isRoot ? null : (itemId as UUID);
      onItemDrop?.(targetFolderId, event);
    },
    [isRoot, itemId, onItemDrop],
  );

  const { isDragging, showDropHighlight, handlers } = useDragState({
    onDragStart: handleItemDragStart,
    onDragOver: onItemDragOver,
    onDrop: handleItemDrop,
  });

  const saveRename = useSupabaseCallback(
    async ({ supabase }, name: string) => {
      const service = new SupabaseFolderService(supabase);

      const { error } = await service.update(itemId as UUID, { name });

      if (error) {
        if (error.message.includes("folder_unique_name")) {
          toast.error("Folder name must be unique");
        } else {
          toast.error("Error when renaming folder");
        }
        throw error;
      } else {
        toast.success("Folder renamed");
      }
    },
    [itemId],
  );

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleRenameStart = (): void => {
    setMenuAnchor(null);
    lastCommittedNameRef.current = local;
    setIsRenaming(true);
  };

  const commitRename = (name: string): void => {
    const cleaned = name.trim();
    if (cleaned === "" || cleaned === lastCommittedNameRef.current) {
      setIsRenaming(false);
      return;
    }

    setLocal(cleaned);
    void persistFolderName(cleaned, lastCommittedNameRef.current);
  };

  const persistFolderName = async (newValue: string, previousValue: string): Promise<void> => {
    try {
      await saveRename(newValue);
      lastCommittedNameRef.current = newValue;
      setIsRenaming(false);
      reloadFolders?.();
    } catch (e) {
      setLocal(previousValue);
      setIsRenaming(false);
      console.error(e);
    }
  };

  useEffect(() => {
    if (isRenaming) {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    }
  }, [isRenaming]);

  const getSharingLink = (): string => {
    const url = new URL(window.location.href);
    url.search = "";
    url.searchParams.set("folder", itemId);
    return url.toString();
  };

  return (
    <TreeItem
      {...rest}
      itemId={itemId}
      slotProps={{
        ...slotProps,
        label: {
          ...slotProps?.label,
          style: { display: "flex", alignItems: "center", gap: 8, width: "100%" },
        },
      }}
      label={
        <Box
          data-menu-open={menuOpen}
          draggable={!isRoot}
          onDragStart={handlers.handleDragStart}
          onDragEnd={handlers.handleDragEnd}
          onDragOver={handlers.handleDragOver}
          onDragEnter={handlers.handleDragEnter}
          onDragLeave={handlers.handleDragLeave}
          onDrop={handlers.handleDrop}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "100%",
            borderRadius: 1,
            padding: "2px 4px",
            margin: "-2px -4px",
            cursor: !isRoot ? (isDragging ? "grabbing" : "grab") : undefined,
            opacity: isDragging ? 0.5 : 1,
            transition: "background-color 0.2s, outline 0.2s",
            ...(showDropHighlight
              ? {
                  backgroundColor: "primary.main",
                  color: "primary.contrastText",
                }
              : {}),
            "& .folder-menu-button": {
              opacity: 0,
              pointerEvents: "none",
              transition: "opacity 120ms ease-in-out",
            },
            "&:hover .folder-menu-button, &:focus-within .folder-menu-button, &[data-menu-open='true'] .folder-menu-button":
              {
                opacity: 1,
                pointerEvents: "auto",
              },
          }}
        >
          <Box
            data-testid={`Tree item ${local}`}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexGrow: 1,
              minWidth: 0,
            }}
          >
            {isRoot ? (
              <PermMediaOutlined
                fontSize="small"
                sx={{
                  color: showDropHighlight ? "primary.main" : undefined,
                  transition: "color 0.2s",
                }}
              />
            ) : (
              <FolderOutlined
                fontSize="small"
                sx={{
                  color: showDropHighlight ? "primary.main" : undefined,
                  transition: "color 0.2s",
                }}
              />
            )}
            {isRenaming ? (
              <TextField
                defaultValue={local}
                inputRef={renameInputRef}
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => {
                  event.stopPropagation();

                  if (event.key === "Enter") {
                    event.preventDefault();
                    if (event.target instanceof HTMLInputElement) {
                      void commitRename(event.target.value);
                    }
                  }

                  if (event.key === "Escape") {
                    setIsRenaming(false);
                  }
                }}
                onBlur={(event) => {
                  event.stopPropagation();
                  void commitRename(event.target.value);
                }}
                aria-label="Rename folder"
                size="small"
                variant="standard"
                sx={{ flexGrow: 1, minWidth: 0 }}
              />
            ) : (
              <ConditionalWrapper
                condition={shouldTruncate}
                wrapper={(children) => (
                  <Tooltip title={local} enterDelay={500} placement="top">
                    {children}
                  </Tooltip>
                )}
              >
                <Typography
                  variant="body2"
                  sx={
                    shouldTruncate
                      ? {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          minWidth: 0,
                        }
                      : undefined
                  }
                >
                  {local}
                </Typography>
              </ConditionalWrapper>
            )}
          </Box>
          {!isRoot && (
            <CustomTreeItemMenu
              menuOpen={menuOpen}
              menuAnchor={menuAnchor}
              onMenuOpen={handleMenuOpen}
              onMenuClose={() => setMenuAnchor(null)}
              onRename={handleRenameStart}
              onGetSharingLink={getSharingLink}
              onCreateSubFolder={() => onCreateSubFolder?.(itemId as UUID)}
              onArchive={() => onArchive?.(itemId as UUID)}
              onMove={() => onMove?.(itemId as UUID)}
            />
          )}
        </Box>
      }
    />
  );
};
